import PropTypes from 'prop-types'
import React, { memo, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { Table } from '../../../components/Table'
import SecurityPostureSecureScore from '../../SecurityPosture/SecureScore/SecureScore'
import { StatusPill } from '../StatusPill'
import { formatComplianceAuditTemplates, getComplianceAuditStatusBadgeVariant, getPartialStatusDetail } from '../utils'
import './ComplianceAuditTable.module.scss'
import { getPostureRationale, getPostureStatusDetails } from '../../SecurityPosture/utils'
import { getPdfVisibleColumnIds } from '../pdfColumns'

const DEFAULT_VISIBLE_COLUMN_IDS = ['statusDetails', 'categories', 'assessments', 'secureScore']

/**
 * Every customize-menu column id this table can render, Security Check/Status excluded (those are
 * `always: true` below). Used to resolve which columns are selected before applying the PDF budget.
 */
const OPTIONAL_COLUMN_IDS = [
  'compliance',
  'statusDetails',
  'categories',
  'portal',
  'rationale',
  'assessments',
  'license',
  'secureScore',
  'notes',
]

const propTypes = {
  /** Rows pre-formatted for PDF/export (see complianceAuditExport.js). */
  tableData: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  /** Hides filters/sort and renders a read-only table for PDF reports. */
  print: PropTypes.bool.isRequired,
  /** Optional column visibility config from the compliance audit customize menu. */
  columnConfig: PropTypes.shape({
    visibleColumnIds: PropTypes.arrayOf(PropTypes.string),
    notesDisplayMode: PropTypes.string,
    showComplianceColumn: PropTypes.bool,
    complianceColumnLabel: PropTypes.string,
  }),
}

const formatNotesCell = (notes, notesDisplayMode, intl) => {
  if (!Array.isArray(notes) || !notes.length) {
    return null
  }

  const latest = [...notes].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0]
  if (!latest) {
    return null
  }

  if (notesDisplayMode === 'statusOnly') {
    if (latest.status === 'custom' && latest.customStatus) {
      return latest.customStatus
    }
    const messageId = {
      risk_accepted: 'COMPLIANCE_AUDIT.CHECK.NOTES.STATUS.RISK_ACCEPTED',
      planned: 'COMPLIANCE_AUDIT.CHECK.NOTES.STATUS.PLANNED',
      comment: 'COMPLIANCE_AUDIT.CHECK.NOTES.STATUS.COMMENT',
      custom: 'COMPLIANCE_AUDIT.CHECK.NOTES.STATUS.CUSTOM',
    }[latest.status]

    return messageId ? intl.formatMessage({ id: messageId, defaultMessage: latest.status }) : latest.status
  }

  return latest.note ?? null
}

function ComplianceAuditTable({ tableData, print, columnConfig }) {
  const intl = useIntl()
  const visibleColumnIds = columnConfig?.visibleColumnIds ?? DEFAULT_VISIBLE_COLUMN_IDS
  const notesDisplayMode = columnConfig?.notesDisplayMode ?? 'fullText'
  const showComplianceColumn = Boolean(columnConfig?.showComplianceColumn)
  const complianceColumnLabel =
    columnConfig?.complianceColumnLabel || intl.formatMessage({ id: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE' })
  const isColumnSelected = (columnId) => {
    if (columnId === 'compliance') {
      return showComplianceColumn
    }

    return visibleColumnIds.includes(columnId)
  }

  // PDF never shows Portal/Notes and caps the total at MAX_PDF_COLUMNS (see pdfColumns.js), keeping
  // the highest-precedence selected columns so the result only depends on which are selected, not
  // the order they were toggled in. The live table shows every selected column with no cap.
  const pdfVisibleColumnIds = print ? getPdfVisibleColumnIds(OPTIONAL_COLUMN_IDS.filter(isColumnSelected)) : null
  const include = (columnId) => (print ? pdfVisibleColumnIds.includes(columnId) : isColumnSelected(columnId))

  const formatNameColumn = (cell) => <span className="Uic-ComplianceAuditTable-securityCheckCell">{cell}</span>

  const formatStatusColumn = (cell, row) =>
    cell ? (
      <div className="Uic-ComplianceAuditTable-statusColumn">
        <div>
          <StatusPill
            variant={getComplianceAuditStatusBadgeVariant(cell, intl)}
            label={cell}
            detail={getPartialStatusDetail(cell, row, intl)}
            wrap
          />
        </div>
        {include('statusDetails') ? (
          <div className="Uic-ComplianceAuditTable-statusDetailsCell">{getPostureStatusDetails(row, intl)}</div>
        ) : null}
      </div>
    ) : null

  const formatTemplatesColumn = (cell, row) => formatComplianceAuditTemplates(row.formattedTemplates)

  const formatSecureScoreColumn = (cell, row) => {
    const { msSecureScore } = row

    if (msSecureScore?.currentScore == null) {
      return null
    }

    if (msSecureScore.maxScore != null) {
      return <SecurityPostureSecureScore {...msSecureScore} />
    }

    return <span>{msSecureScore.currentScore}</span>
  }

  const columns = useMemo(() => {
    const allColumns = [
      {
        dataField: 'formattedComplianceRef',
        text: intl.formatMessage({ id: 'COMPLIANCE_AUDIT.REF_NUMBER' }),
        headerStyle: () => ({ width: '8%' }),
        pdfWidthWeight: 8,
        columnId: 'compliance',
      },
      {
        dataField: 'formattedCompliance',
        text: complianceColumnLabel,
        headerStyle: () => ({ width: '18%' }),
        pdfWidthWeight: 18,
        columnId: 'compliance',
      },
      {
        dataField: 'formattedName',
        text: intl.formatMessage({ id: 'COMPLIANCE_AUDIT.SECURITY_CHECK' }),
        formatter: formatNameColumn,
        sort: !print,
        headerStyle: () => ({ width: '24%' }),
        pdfWidthWeight: 24,
        always: true,
      },
      {
        dataField: 'formattedStatus',
        text: intl.formatMessage({ id: 'COMPLIANCE_AUDIT.STATUS' }),
        formatter: formatStatusColumn,
        sort: !print,
        headerStyle: () => ({ width: '20%' }),
        pdfWidthWeight: 20,
        always: true,
      },
      {
        dataField: 'description',
        text: intl.formatMessage({ id: 'COMPLIANCE_AUDIT.RATIONALE' }),
        formatter: (_, row) => getPostureRationale(row, intl),
        headerStyle: () => ({ width: '28%' }),
        pdfWidthWeight: 28,
        columnId: 'rationale',
      },
      {
        dataField: 'category',
        text: intl.formatMessage({ id: 'COMPLIANCE_AUDIT.CATEGORIES' }),
        sort: !print,
        headerStyle: () => ({ width: '10%' }),
        pdfWidthWeight: 10,
        columnId: 'categories',
      },
      {
        dataField: 'portal',
        text: intl.formatMessage({ id: 'COMPLIANCE_AUDIT.PORTAL' }),
        formatter: (cell) => (Array.isArray(cell) ? cell.join(', ') : cell),
        headerStyle: () => ({ width: '10%' }),
        pdfWidthWeight: 10,
        columnId: 'portal',
      },
      {
        dataField: 'formattedTemplates',
        text: intl.formatMessage({ id: 'COMPLIANCE_AUDIT.TEMPLATES' }),
        formatter: formatTemplatesColumn,
        headerStyle: () => ({ width: '10%' }),
        pdfWidthWeight: 10,
        columnId: 'assessments',
      },
      {
        dataField: 'license',
        text: intl.formatMessage({ id: 'COMPLIANCE_AUDIT.M365_LICENSE_REQ' }),
        headerStyle: () => ({ width: '10%' }),
        pdfWidthWeight: 10,
        columnId: 'license',
      },
      {
        dataField: 'msSecureScore.currentScore',
        text: intl.formatMessage({ id: 'COMPLIANCE_AUDIT.SECURE_SCORE' }),
        formatter: formatSecureScoreColumn,
        sort: !print,
        headerStyle: () => ({ width: '8%' }),
        pdfWidthWeight: 8,
        columnId: 'secureScore',
      },
      {
        dataField: 'notes',
        text: intl.formatMessage({ id: 'GENERAL.NOTES' }),
        formatter: (_, row) => formatNotesCell(row.notes, notesDisplayMode, intl),
        headerStyle: () => ({ width: '16%' }),
        pdfWidthWeight: 16,
        columnId: 'notes',
      },
    ]

    const renderedColumns = allColumns.filter((column) => column.always || include(column.columnId))

    if (!print) {
      return renderedColumns
    }

    // Widths above are static per-column shares that only add up to 100% when every column is
    // shown. Rescale by weight so a PDF with fewer than MAX_PDF_COLUMNS columns still fills the
    // full page width instead of leaving the layout gapped.
    const totalPdfWeight = renderedColumns.reduce((sum, column) => sum + column.pdfWidthWeight, 0)
    return renderedColumns.map((column) => ({
      ...column,
      headerStyle: () => ({ width: `${(column.pdfWidthWeight / totalPdfWeight) * 100}%` }),
    }))
  }, [intl, print, visibleColumnIds, notesDisplayMode, showComplianceColumn, complianceColumnLabel]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="Uic-ComplianceAuditTable-tableWrapper">
      <Table data={tableData} keyField="rowId" columns={columns} hideHeader hidePagination readOnly={print} />
    </div>
  )
}

ComplianceAuditTable.propTypes = propTypes

export default memo(ComplianceAuditTable)
